import { ListItemIcon, MenuItem, ListItemButton, Typography, useTheme } from '@mui/material'
import { Link, useLocation, useParams } from 'react-router-dom'
import { MenuItemUnifiedProps } from './types'
import { AnalyticsEventName, AnalyticsTracker } from '@/infra/tracking'

export function MenuItemUnified({
  variant,
  route,
  availableIn = [],
  icon: Icon,
  className,
  onClick,
  onClose,
  sx: externalSx,
  ...props
}: MenuItemUnifiedProps) {
  const theme = useTheme()
  const location = useLocation()
  const params = useParams()

  // Check if current route matches this menu item
  const allRoutes = [route, ...availableIn]
  const isActive: boolean = allRoutes.some(
    r => (r.computeValue ? r.computeValue(params) : r.value) === location.pathname
  )

  const handleClick = () => {
    if (route.name) {
      AnalyticsTracker.trackEvent(AnalyticsEventName.HEADER_NAVIGATE, {
        destination: route.name
      })
    }
    if (onClick) {
      onClick()
    }
    onClose()
  }

  // Variant-specific styling
  const getStyles = () => {
    const baseStyles =
      variant === 'desktop' ? { py: theme.spacing(7 / 8), px: theme.spacing(3) } : {}
    return { ...baseStyles, ...externalSx }
  }

  const iconColor = isActive ? 'primary' : undefined
  const iconSx = isActive ? {} : { color: 'grey.900' }

  const textColor = isActive ? 'primary' : 'grey.900'
  const textVariant = variant === 'desktop' ? 'body1' : 'h6'
  const textSx =
    variant === 'desktop'
      ? { fontWeight: isActive ? 'bold' : 'inherit' }
      : {
          fontWeight: isActive ? 700 : 'inherit',
          ml: theme.spacing(3 / 2)
        }

  // Render desktop variant (MenuItem)
  if (variant === 'desktop') {
    return (
      <MenuItem
        component={Link}
        to={route.value}
        sx={getStyles()}
        onClick={handleClick}
        className={className}
        {...props}
      >
        {Icon && (
          <ListItemIcon>
            <Icon color={iconColor} sx={iconSx} />
          </ListItemIcon>
        )}
        <Typography variant={textVariant} color={textColor} sx={textSx}>
          {route.title}
        </Typography>
      </MenuItem>
    )
  }

  // Render mobile variant (ListItemButton)
  return (
    <ListItemButton
      component={Link}
      to={route.value}
      onClick={handleClick}
      className={className}
      {...props}
    >
      {Icon && <Icon color={iconColor} sx={iconSx} />}
      <Typography variant={textVariant} color={textColor} sx={textSx}>
        {route.title}
      </Typography>
    </ListItemButton>
  )
}
